import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { featuredSlides } from "@/mocks/site";
import { formatDuration, getVideoById, typeLabel, videoPath } from "@/lib/videos";

const slides = featuredSlides
  .map((item) => ({ ...item, video: getVideoById(item.videoId) }))
  .filter((item) => Boolean(item.video));

const heroImages: Record<string, string> = {
  "v-017": "https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20luxurious%20vintage%20hotel%20corridor%20with%20deep%20crimson%20carpet%2C%20warm%20amber%20wall%20sconces%20casting%20long%20dramatic%20shadows%2C%20rain%20streaked%20tall%20windows%2C%20dark%20moody%20atmosphere%2C%20film%20noir%20aesthetic%2C%20rich%20texture%2C%20editorial%20cinematography&width=1920&height=1080&seq=passion-hero-017&orientation=landscape",
  "v-002": "https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20rainy%20hong%20kong%20street%20at%20night%20with%20dozens%20of%20glowing%20red%20neon%20signs%20reflecting%20on%20wet%20asphalt%2C%20dense%20urban%20canyon%2C%20steam%20and%20mist%2C%20deep%20black%20shadows%20with%20crimson%20and%20gold%20highlights%2C%20anamorphic%20film%20look&width=1920&height=1080&seq=passion-hero-002&orientation=landscape",
  "v-012": "https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20dim%20cafe%20table%20with%20a%20white%20coffee%20cup%20bearing%20a%20red%20lipstick%20mark%2C%20single%20warm%20spotlight%20from%20above%2C%20deep%20black%20surroundings%2C%20dramatic%20chiaroscuro%20lighting%2C%20minimal%20elegant%20composition%2C%20moody%20film%20still&width=1920&height=1080&seq=passion-hero-012&orientation=landscape",
  "v-004": "https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20bangkok%20rooftop%20at%20dusk%20with%20heat%20haze%20and%20a%20huge%20orange%20crimson%20sky%2C%20silhouetted%20city%20skyline%20and%20water%20tanks%2C%20warm%20dusty%20air%2C%20film%20grain%20texture%2C%20anamorphic%20lens%20flare%2C%20moody%20atmosphere&width=1920&height=1080&seq=passion-hero-004&orientation=landscape",
  "v-018": "https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20lone%20wooden%20boat%20on%20a%20dark%20calm%20sea%20under%20a%20deep%20red%20dusk%20sky%2C%20soft%20mist%20over%20water%2C%20gentle%20ripples%20reflecting%20crimson%20light%2C%20minimal%20melancholic%20elegant%20composition%2C%20film%20photography&width=1920&height=1080&seq=passion-hero-018&orientation=landscape",
};

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);

  const go = useCallback((index: number) => {
    setActive(((index % slides.length) + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => setActive((prev) => (prev + 1) % slides.length), 6000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? active + 1 : active - 1);
    }
    touchStart.current = null;
  };

  return (
    <section
      className="relative w-full h-[560px] md:h-[680px] lg:h-[760px] overflow-hidden bg-background-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="精选内容轮播"
    >
      {slides.map((slide, index) => (
        <div
          key={slide.videoId}
          className={`absolute inset-0 transition-opacity duration-700 ${index === active ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <div className="absolute inset-0 w-full h-full">
            <img
              src={heroImages[slide.videoId] || slide.video?.cover || ""}
              alt={`${slide.video?.title} 主视觉海报`}
              title={`${slide.video?.title} ${slide.video?.category} 精选推荐`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/70 to-background-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/50" />
        </div>
      ))}

      <div className="relative z-10 h-full flex items-center">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-2xl">
            {slides.map((slide, index) =>
              index === active ? (
                <div key={slide.videoId} className="animate-fade-up">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="h-[2px] w-8 bg-primary-500" />
                    <span className="text-xs tracking-[0.28em] text-accent-400 font-label uppercase">
                      {slide.kicker}
                    </span>
                  </div>
                  <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] text-foreground-50">
                    {slide.video?.title}
                  </h1>
                  <p className="mt-4 text-sm md:text-base text-foreground-300 max-w-xl leading-relaxed">
                    {slide.tagline}
                  </p>
                  <div className="mt-5 flex items-center gap-3 flex-wrap text-xs text-foreground-400">
                    <span className="px-2 py-0.5 rounded-sm bg-primary-500/90 text-background-50 font-medium">
                      {typeLabel(slide.video!.type)}
                    </span>
                    <span>{slide.video?.region}</span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-600" />
                    <span>{slide.video?.year}</span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-600" />
                    <span>{formatDuration(slide.video!.duration)}</span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-600" />
                    <span className="flex items-center gap-1 text-accent-400">
                      <i className="ri-star-fill" />
                      {slide.video?.rating.toFixed(1)}
                    </span>
                  </div>
                  <div className="mt-8 flex items-center gap-3">
                    <Link
                      to={videoPath(slide.videoId)}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-fill text-lg" />
                      查看详情
                    </Link>
                    <Link
                      to="/category/movie"
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-foreground-200/35 text-foreground-100 hover:bg-foreground-50/10 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                    >
                      全部分类
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              ) : null,
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 md:bottom-8 left-4 md:left-8 lg:left-12 right-4 md:right-8 z-20 flex items-end justify-between gap-4">
        <div className="flex items-center gap-2">
          {slides.map((slide, index) => (
            <button
              key={slide.videoId}
              type="button"
              onClick={() => go(index)}
              aria-label={`切换到第 ${index + 1} 张`}
              className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                index === active ? "w-10 bg-primary-500" : "w-5 bg-foreground-400/40 hover:bg-foreground-300/70"
              }`}
            />
          ))}
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(active - 1)}
            aria-label="上一张"
            className="w-9 h-9 rounded-full border border-foreground-200/30 flex items-center justify-center text-foreground-100 hover:bg-foreground-50/10 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <span className="font-label text-xs tracking-widest text-foreground-400 w-10 text-center">
            {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
          <button
            type="button"
            onClick={() => go(active + 1)}
            aria-label="下一张"
            className="w-9 h-9 rounded-full border border-foreground-200/30 flex items-center justify-center text-foreground-100 hover:bg-foreground-50/10 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>
    </section>
  );
}